import {
  RC_BALANCE_HEALTH_FAX_NUMBER,
  RC_BALANCE_HEALTH_FAX_NUMBER_2,
  RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER,
  RC_BALANCE_HEALTH_DESERT_FAX_NUMBER,
  RC_BALANCE_HEALTH_IFAC_FAX_NUMBER,
  RC_BALANCE_HEALTH_WFAI_FAX_NUMBER,
  RC_BALANCE_HEALTH_FCC_FAX_NUMBER,
  RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER,
  RC_BALANCE_HEALTH_1F2F_FAX_NUMBER,
  RC_BALANCE_HEALTH_FASWM_FAX_NUMBER,
  RC_BALANCE_HEALTH_LVFAC_FAX_NUMBER,
  RC_BALANCE_HEALTH_UFAI_FAX_NUMBER,
  RC_BALANCE_HEALTH_PRECISION_FAX_NUMBER,
  RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  RC_BALANCE_HEALTH_SFAC_FAX_NUMBER,
  RC_BALANCE_HEALTH_PPO_FAX_NUMBER,
  RC_BALANCE_HEALTH_VPG_FAX_NUMBER,
  getBalanceHealthExistingFaxNumber,
} from "@/src/lib/ringcentral";
import { Classification } from "@/src/models/constants/classifications";

/**
 * Fax-line–based routing for Balance Health, transcribed from the ops "Index Mapping"
 * spreadsheet. Each classification has a primary route plus optional conditional routes
 * (the spreadsheet's `Logic` column), and each route carries a **per-fax-line assignee** map.
 *
 * The inbound fax line determines the assignee and whether a task is created: when the cell
 * for a (classification, route, fax line) is empty — i.e. the `BhFaxLine` key is absent from
 * `assignees` — no ModMed task is created and CloudCruise is not triggered (the document is
 * still uploaded). See `configuration.ts` (`getBalanceHealthDocumentInstructions`) and
 * `balanceTaskSkipCheckStep`.
 *
 * This table owns **assignment + the upload folder** only. The document label and task type
 * still come from the per-classification logic in `configuration.ts`
 * (`getBalanceHealthUtahDocumentInstructionsByClassification`); `folder` here is consumed by the
 * EHR integration's folder resolution.
 */

// ---------------------------------------------------------------------------
// Fax lines
// ---------------------------------------------------------------------------

/** Spreadsheet location columns. All lines are now provisioned and wired to RingCentral numbers. */
export enum BhFaxLine {
  UT = "UT",
  SOCAL = "SoCal",
  FCC = "FCC",
  DESERT = "Desert",
  AFAPS = "AFAPS",
  ONE_F_TWO_F = "1F2F",
  FASWM = "FASWM",
  UFAI = "UFAI",
  IFAC = "IFAC",
  PRECISION = "Precision",
  NV = "NV",
  FACR = "FACR",
  WFAI = "WFAI",
  BAFC = "BAFC",
  SFAC = "SFAC",
  PPO = "PPO",
  VPG = "VPG",
}

/** Existing inbound fax numbers → location column. */
export const BH_FAX_NUMBER_TO_LINE: Record<string, BhFaxLine> = {
  [RC_BALANCE_HEALTH_FAX_NUMBER]: BhFaxLine.UT,
  [RC_BALANCE_HEALTH_FAX_NUMBER_2]: BhFaxLine.SOCAL,
  [RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER]: BhFaxLine.AFAPS,
  [RC_BALANCE_HEALTH_DESERT_FAX_NUMBER]: BhFaxLine.DESERT,
  [RC_BALANCE_HEALTH_IFAC_FAX_NUMBER]: BhFaxLine.IFAC,
  [RC_BALANCE_HEALTH_WFAI_FAX_NUMBER]: BhFaxLine.WFAI,
  [RC_BALANCE_HEALTH_FCC_FAX_NUMBER]: BhFaxLine.FCC,
  [RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER]: BhFaxLine.FACR,
  [RC_BALANCE_HEALTH_1F2F_FAX_NUMBER]: BhFaxLine.ONE_F_TWO_F,
  [RC_BALANCE_HEALTH_FASWM_FAX_NUMBER]: BhFaxLine.FASWM,
  [RC_BALANCE_HEALTH_LVFAC_FAX_NUMBER]: BhFaxLine.NV,
  [RC_BALANCE_HEALTH_UFAI_FAX_NUMBER]: BhFaxLine.UFAI,
  [RC_BALANCE_HEALTH_PRECISION_FAX_NUMBER]: BhFaxLine.PRECISION,
  [RC_BALANCE_HEALTH_BAFC_FAX_NUMBER]: BhFaxLine.BAFC,
  [RC_BALANCE_HEALTH_SFAC_FAX_NUMBER]: BhFaxLine.SFAC,
  [RC_BALANCE_HEALTH_PPO_FAX_NUMBER]: BhFaxLine.PPO,
  [RC_BALANCE_HEALTH_VPG_FAX_NUMBER]: BhFaxLine.VPG,
};

/** Resolve the inbound fax number to a location column, defaulting to UT for unknown lines
 * (preserves the prior "unknown line → Utah instructions" behavior). */
export function getBhFaxLine(faxRecipientNumber: string | null | undefined): BhFaxLine {
  const existingFaxNumber = faxRecipientNumber ? getBalanceHealthExistingFaxNumber(faxRecipientNumber) : null;

  return (existingFaxNumber && BH_FAX_NUMBER_TO_LINE[existingFaxNumber]) || BhFaxLine.UT;
}

/**
 * Recall type keyed by fax line — parallel to BALANCE_HEALTH_RECALL_TYPE_BY_FAX_LINE (which is
 * keyed by raw fax number). Used for NPI-based routing where we resolve a fax line directly
 * from the receiving provider rather than from the inbound fax number.
 */
export const BH_FAX_LINE_TO_RECALL_TYPE: Record<BhFaxLine, string> = {
  [BhFaxLine.UT]: "Referral AFAC",
  [BhFaxLine.SOCAL]: "Referral SCFC",
  [BhFaxLine.FCC]: "Referral TFCC",
  [BhFaxLine.DESERT]: "Referral DFAA",
  [BhFaxLine.AFAPS]: "Referral AFAPS",
  [BhFaxLine.ONE_F_TWO_F]: "Referral 1F2F",
  [BhFaxLine.FASWM]: "Referral FASWM",
  [BhFaxLine.UFAI]: "Referral UFAI",
  [BhFaxLine.IFAC]: "Referral IFAC",
  [BhFaxLine.PRECISION]: "Referral UFAI",
  [BhFaxLine.NV]: "Referral NVFAC",
  [BhFaxLine.FACR]: "Referral FACR",
  [BhFaxLine.WFAI]: "Referral WFAI",
  [BhFaxLine.BAFC]: "Referral BAFC",
  [BhFaxLine.SFAC]: "Referral SFAC",
  [BhFaxLine.PPO]: "Referral PPO",
  [BhFaxLine.VPG]: "Referral VPG",
};

/**
 * Receiving provider NPI → Balance Health fax line for providers whose practice location is
 * known. Used to route referrals to the receiving provider's location rather than defaulting to
 * the inbound fax line. Covers providers whose practice group is explicitly identified; all
 * other Balance Health providers (and any non-Balance-Health provider) fall back to the fax line.
 */
export const BH_NPI_TO_FAX_LINE: Record<string, BhFaxLine> = {
  // 1Foot 2Foot Centre for Foot & Ankle Care (Virginia / ONE_F_TWO_F)
  "1154416352": BhFaxLine.ONE_F_TWO_F,
  "1972246114": BhFaxLine.ONE_F_TWO_F,
  "1124401948": BhFaxLine.ONE_F_TWO_F,
  "1437692530": BhFaxLine.ONE_F_TWO_F,
  "1144226853": BhFaxLine.ONE_F_TWO_F,
  "1386205839": BhFaxLine.ONE_F_TWO_F,
  "1750744355": BhFaxLine.ONE_F_TWO_F,
  "1275875718": BhFaxLine.ONE_F_TWO_F,
  "1346987948": BhFaxLine.ONE_F_TWO_F,
  "1770145856": BhFaxLine.ONE_F_TWO_F,
  // Foot & Ankle Specialists of West Michigan, PLLC
  "1447281662": BhFaxLine.FASWM,
  "1619388857": BhFaxLine.FASWM,
  "1881113496": BhFaxLine.FASWM,
  "1912318502": BhFaxLine.FASWM,
  "1023053436": BhFaxLine.FASWM,
  "1730749961": BhFaxLine.FASWM,
  "1679501290": BhFaxLine.FASWM,
  "1326084013": BhFaxLine.FASWM,
  "1588907067": BhFaxLine.FASWM,
  "1609506740": BhFaxLine.FASWM,
  "1063435659": BhFaxLine.FASWM,
  // University Foot and Ankle Institute
  "1952310799": BhFaxLine.UFAI,
  "1215946157": BhFaxLine.UFAI,
  "1922568369": BhFaxLine.UFAI,
  "1659857654": BhFaxLine.UFAI,
  "1215518105": BhFaxLine.UFAI,
  "1134708100": BhFaxLine.UFAI,
  "1720010200": BhFaxLine.UFAI,
  "1437379583": BhFaxLine.UFAI,
  "1699700518": BhFaxLine.UFAI,
  "1417518374": BhFaxLine.UFAI,
  "1982348280": BhFaxLine.UFAI,
  "1790309672": BhFaxLine.UFAI,
  "1669081709": BhFaxLine.UFAI,
  // CA Precision Surgery Center
  "1710509336": BhFaxLine.PRECISION,
  "1366534216": BhFaxLine.PRECISION,
  "1104998434": BhFaxLine.PRECISION,
  // NV Las Vegas - Flamingo
  "1487684189": BhFaxLine.NV,
  "1427006592": BhFaxLine.NV,
  "1083725436": BhFaxLine.NV,
  "1942292883": BhFaxLine.NV,
  "1053722702": BhFaxLine.NV,
  "1245222181": BhFaxLine.NV,
  "1558691071": BhFaxLine.NV,
  "1194068767": BhFaxLine.NV,
  "1548252224": BhFaxLine.NV,
  "1689802332": BhFaxLine.NV,
  "1902331705": BhFaxLine.NV,
  "1255411419": BhFaxLine.NV, // Bryan Berghout
  "1568098911": BhFaxLine.NV, // Nathan Fletcher
  // WA IFAC Mount Vernon
  "1407827454": BhFaxLine.IFAC, // Randolph Anderson
  "1265094700": BhFaxLine.IFAC, // Nicholas Chang
  // WA IFAC Bremerton/Port Orchard
  "1205818069": BhFaxLine.IFAC, // David Gent
  "1073735262": BhFaxLine.IFAC, // Doan Allan
  "1003805482": BhFaxLine.IFAC, // Landino, Thomas, DPM
  "1811055890": BhFaxLine.IFAC, // Doan, Allan, DPM
  "1841315736": BhFaxLine.IFAC, // Chang, Thomas, DPM
  "1932308913": BhFaxLine.IFAC, // Bernstein, David, DPM
  // CA BAFC Alameda
  "1780252213": BhFaxLine.BAFC,
  "1508314808": BhFaxLine.BAFC,
  "1669051116": BhFaxLine.BAFC,
  "1568991073": BhFaxLine.BAFC,
  "1487143640": BhFaxLine.BAFC,
  "1467973230": BhFaxLine.BAFC,
  "1467835991": BhFaxLine.BAFC,
  "1063776466": BhFaxLine.BAFC,
  "1033266721": BhFaxLine.BAFC,
  "1215940283": BhFaxLine.BAFC,
  "1508386038": BhFaxLine.BAFC,
  "1255795050": BhFaxLine.BAFC,
  "1457996753": BhFaxLine.BAFC,
  "1215020730": BhFaxLine.BAFC,
  "1104433226": BhFaxLine.BAFC,
  "1609307180": BhFaxLine.BAFC,
  "1891097655": BhFaxLine.BAFC,
  "1265012348": BhFaxLine.BAFC,
  "1023112554": BhFaxLine.BAFC,
  "1619996105": BhFaxLine.BAFC,
  "1578121927": BhFaxLine.BAFC,
  "1609967413": BhFaxLine.BAFC,
  "1144071093": BhFaxLine.BAFC,
  "1841788320": BhFaxLine.BAFC,
  "1760580310": BhFaxLine.BAFC,
  "1023011459": BhFaxLine.BAFC,
  "1629564869": BhFaxLine.BAFC,
  "1679712624": BhFaxLine.BAFC,
  "1851828594": BhFaxLine.BAFC,
  "1962595611": BhFaxLine.BAFC,
  "1942731021": BhFaxLine.BAFC,
  "1124641436": BhFaxLine.BAFC,
  "1063729630": BhFaxLine.BAFC,
  "1952133621": BhFaxLine.BAFC,
  "1962077693": BhFaxLine.BAFC,
  "1558425595": BhFaxLine.BAFC,
  "1538232731": BhFaxLine.BAFC,
  "1619320769": BhFaxLine.BAFC,
  "1598573289": BhFaxLine.BAFC,
  "1518996446": BhFaxLine.BAFC,
  "1992049571": BhFaxLine.BAFC,
  "1982187027": BhFaxLine.BAFC,
  "1619751450": BhFaxLine.BAFC,
  "1508665316": BhFaxLine.BAFC,
  "1801117874": BhFaxLine.BAFC,
  "1528053535": BhFaxLine.BAFC,
  "1730167370": BhFaxLine.BAFC,
  "1174189369": BhFaxLine.BAFC,
  "1598210510": BhFaxLine.BAFC,
  "1134706625": BhFaxLine.BAFC,
  "1952438566": BhFaxLine.BAFC,
  "1013960806": BhFaxLine.BAFC, // Poggio, Anthony, DPM
  "1023154622": BhFaxLine.BAFC, // Patel, Gitanjali, DPM
  "1023192721": BhFaxLine.BAFC, // Reyzelman, Alexander, DPM
  "1043072358": BhFaxLine.BAFC, // Seun, Jonathan, DPM
  "1073707923": BhFaxLine.BAFC, // Mcaloon, Carolyn, DPM
  "1093778714": BhFaxLine.BAFC, // Sheth, Ami, DPM
  "1104211366": BhFaxLine.BAFC, // Potter, Jessica, DPM
  "1124182316": BhFaxLine.BAFC, // Van Woy, Teresa, DPM
  "1124761705": BhFaxLine.BAFC, // Svidler, Frances, DPM
  "1134229735": BhFaxLine.BAFC, // Nix, Kelly, DPM
  "1174674436": BhFaxLine.BAFC, // Surdock, Nicole, DPM
  "1184949661": BhFaxLine.BAFC, // Walker, Judianne, DPM
  "1215383088": BhFaxLine.BAFC, // Kaur, Amrita, DPM
  "1235289042": BhFaxLine.BAFC, // Jordan, Thomas, DPM
  "1336230127": BhFaxLine.BAFC, // Leviant, Curtis, DPM
  "1376958405": BhFaxLine.BAFC, // Marshall, Christyn, DPM
  "1427684422": BhFaxLine.BAFC, // Nguyen, Julian, DPM
  "1427685395": BhFaxLine.BAFC, // Pham, Gia Cat, DPM
  "1437437381": BhFaxLine.BAFC, // Sternak, Adam, DPM
  "1447836358": BhFaxLine.BAFC, // Liew, Victoria, DPM
  "1457887556": BhFaxLine.BAFC, // Leung, Bobby, DPM
  "1528099439": BhFaxLine.BAFC, // Rolfes, Richard, DPM
  "1538393608": BhFaxLine.BAFC, // Nguyen, Anthony, DPM
  "1588854723": BhFaxLine.BAFC, // Pandya, Sima, DPM
  "1609187392": BhFaxLine.BAFC, // Thomas, Ryan, DPM
  "1609598853": BhFaxLine.BAFC, // Rashid, Saad, DPM
  "1639416209": BhFaxLine.BAFC, // Sellers, Sahra, DPM
  "1699487215": BhFaxLine.BAFC, // Tavaf, Misha, DPM
  "1710567342": BhFaxLine.BAFC, // Limcaoco, Patricia Maria, DPM
  "1730948886": BhFaxLine.BAFC, // Rasiah, Andrew, DPM
  "1740820786": BhFaxLine.BAFC, // Baccam, Brandon, DPM
  "1760970081": BhFaxLine.BAFC, // Garcia, Morgan, DPM
  "1801201587": BhFaxLine.BAFC, // Lam, Jenny, DPM
  "1811006596": BhFaxLine.BAFC, // Mitnick, Joshua, DPM
  "1821096785": BhFaxLine.BAFC, // Kim, Joseph, DPM
  "1932541836": BhFaxLine.BAFC, // Shanks, Sky, DPM
  "1932841871": BhFaxLine.BAFC, // Singh, Rajan, DPM
  // CA BAFC Davis (SFAC) — also covers Julian Nguyen, Liliya Parkman, Dustin Theis (dual SFAC/PPO)
  "1053602540": BhFaxLine.SFAC,
  "1053727529": BhFaxLine.SFAC,
  "1235511627": BhFaxLine.SFAC,
  "1902898059": BhFaxLine.SFAC,
  "1568807212": BhFaxLine.SFAC,
  "1205907227": BhFaxLine.SFAC,
  "1730748401": BhFaxLine.SFAC,
  "1780067066": BhFaxLine.SFAC,
  "1912468497": BhFaxLine.SFAC,
  "1548838972": BhFaxLine.SFAC,
  "1376568774": BhFaxLine.SFAC,
  "1669009791": BhFaxLine.SFAC,
  "1043589211": BhFaxLine.SFAC,
  "1942321138": BhFaxLine.SFAC,
  "1174614051": BhFaxLine.SFAC, // Hook, Ernest, DPM
  "1235219353": BhFaxLine.SFAC, // Larrucea, Samuel
  "1376387605": BhFaxLine.SFAC, // Silverio, George, DPM
  // CA BAFC Folsom (PPO)
  "1023545407": BhFaxLine.PPO,
  "1033642335": BhFaxLine.PPO,
  "1740785021": BhFaxLine.PPO,
  "1841925039": BhFaxLine.PPO,
  "1518523489": BhFaxLine.PPO,
  "1558654921": BhFaxLine.PPO,
  "1235391798": BhFaxLine.PPO, // Fujii, Scott, MD
  "1043874431": BhFaxLine.PPO, // Sheu, Jonathan, MD
  "1851083430": BhFaxLine.PPO, // O'Keefe, Connor, PA
  // CA BAFC Modesto (VPG)
  "1962145359": BhFaxLine.VPG,
  "1568660439": BhFaxLine.VPG,
  "1053429027": BhFaxLine.VPG,
  "1538787395": BhFaxLine.VPG,
  "1356710974": BhFaxLine.VPG,
  "1730568163": BhFaxLine.VPG,
  // FASWM (MI) — West Michigan
  "1760121776": BhFaxLine.FASWM, // Brown, Andrew, DPM
  "1790714277": BhFaxLine.FASWM, // David, Michael, DPM
  // SoCal (SCFC)
  "1508029018": BhFaxLine.SOCAL, // Steklachich, Kate, DPM
  "1598729923": BhFaxLine.SOCAL, // Paolercio, Nancy, DPM
  // UT AFAC
  "1295221869": BhFaxLine.UT, // Kiser, Christopher, DPM
  "1366079493": BhFaxLine.UT, // Smith, Christian, DPM
  // AFAPS (Artisan)
  "1316766850": BhFaxLine.AFAPS, // Kim, Christine, DPM
  // WFAI (Weil)
  "1316196348": BhFaxLine.WFAI, // Klein, Erin
  "1780971523": BhFaxLine.WFAI, // Siddiqui, Bilal, DPM
  "1942208558": BhFaxLine.WFAI, // Weil Jr, Lowell, DPM
  "1992585558": BhFaxLine.WFAI, // Rane, Rashmi, PT
};

/**
 * Returns the Balance Health fax line for a receiving provider NPI, or null if the provider is
 * not found in the known-location mapping. A null result means the caller should fall back to
 * the inbound fax line for routing.
 */
export function getBhFaxLineFromNpi(npi: string | null | undefined): BhFaxLine | null {
  if (!npi) return null;

  return BH_NPI_TO_FAX_LINE[npi] ?? null;
}

// ---------------------------------------------------------------------------
// Assignee sentinels
// ---------------------------------------------------------------------------

/** Sentinel assignees resolved per-section/per-fax-line in `configuration.ts`
 * (`resolveBhAssignee`). Any other string is a literal ModMed assignee/queue/email. */
export const ASSIGNEE = {
  /** Receiving provider's "Physician User" (by NPI). */
  PHYSICIAN: "@physician",
  /** Receiving provider's "MA User" (by NPI), falling back to the physician. */
  LOCATION_MA: "@location_ma",
  /** Location front desk, falling back to the region's ops manager. */
  LOCATION_FRONT_DESK: "@location_front_desk",
  /** Region medical-records queue. */
  LOCATION_MEDICAL_RECORDS: "@location_medical_records",
  /** Region ops manager. */
  LOCATION_OPS_MANAGER: "@location_ops_manager",
} as const;

const P = ASSIGNEE.PHYSICIAN;
const MA = ASSIGNEE.LOCATION_MA;
const FD = ASSIGNEE.LOCATION_FRONT_DESK;
const MR = ASSIGNEE.LOCATION_MEDICAL_RECORDS;
const OPS = ASSIGNEE.LOCATION_OPS_MANAGER;

type BhAssignees = Partial<Record<BhFaxLine, string>>;

// Preset: every line assigned to the receiving provider. Spread + override for the common
// "mostly physician, a few location overrides" pattern.
const PHYS_ALL: BhAssignees = {
  [BhFaxLine.UT]: P,
  [BhFaxLine.SOCAL]: P,
  [BhFaxLine.FCC]: P,
  [BhFaxLine.DESERT]: P,
  [BhFaxLine.AFAPS]: P,
  [BhFaxLine.ONE_F_TWO_F]: P,
  [BhFaxLine.FASWM]: P,
  [BhFaxLine.UFAI]: P,
  [BhFaxLine.IFAC]: P,
  [BhFaxLine.PRECISION]: P,
  [BhFaxLine.NV]: P,
  [BhFaxLine.FACR]: P,
  [BhFaxLine.WFAI]: P,
  [BhFaxLine.BAFC]: P,
  [BhFaxLine.SFAC]: P,
  [BhFaxLine.PPO]: P,
  [BhFaxLine.VPG]: P,
};

// Disability/FMLA share this primary assignment row.
const DISABILITY_PRIMARY: BhAssignees = {
  [BhFaxLine.UT]: P,
  [BhFaxLine.SOCAL]: P,
  [BhFaxLine.FCC]: P,
  [BhFaxLine.DESERT]: P,
  [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
  [BhFaxLine.ONE_F_TWO_F]: "Davis, Kesha",
  [BhFaxLine.FASWM]: "Perski, Kyleigh",
  [BhFaxLine.UFAI]: MA,
  [BhFaxLine.IFAC]: MA,
  [BhFaxLine.PRECISION]: FD,
  [BhFaxLine.NV]: P,
  [BhFaxLine.FACR]: MA,
  [BhFaxLine.WFAI]: P,
  [BhFaxLine.BAFC]: FD,
  [BhFaxLine.SFAC]: FD,
  [BhFaxLine.PPO]: FD,
  [BhFaxLine.VPG]: FD,
};

// OTHER admin types route to the region ops manager (WFAI has no destination).
const OTHER_OPS_ALL: BhAssignees = {
  [BhFaxLine.UT]: OPS,
  [BhFaxLine.SOCAL]: OPS,
  [BhFaxLine.FCC]: OPS,
  [BhFaxLine.DESERT]: OPS,
  [BhFaxLine.AFAPS]: OPS,
  [BhFaxLine.ONE_F_TWO_F]: OPS,
  [BhFaxLine.FASWM]: OPS,
  [BhFaxLine.UFAI]: OPS,
  [BhFaxLine.IFAC]: OPS,
  [BhFaxLine.PRECISION]: OPS,
  [BhFaxLine.NV]: OPS,
  [BhFaxLine.FACR]: OPS,
  [BhFaxLine.BAFC]: OPS,
  [BhFaxLine.SFAC]: OPS,
  [BhFaxLine.PPO]: OPS,
  [BhFaxLine.VPG]: OPS,
};

// Clearance request / response: most lines go to the physician; VA and MI COB Surgery queues
// own 1F2F and FASWM respectively.
const CLEARANCE_ASSIGNEES: BhAssignees = {
  [BhFaxLine.UT]: P,
  [BhFaxLine.SOCAL]: P,
  [BhFaxLine.FCC]: P,
  [BhFaxLine.DESERT]: P,
  [BhFaxLine.AFAPS]: P,
  [BhFaxLine.ONE_F_TWO_F]: "VA COB Surgery",
  [BhFaxLine.FASWM]: "MI COB Surgery",
  [BhFaxLine.UFAI]: P,
  [BhFaxLine.IFAC]: P,
  [BhFaxLine.PRECISION]: P,
  [BhFaxLine.NV]: P,
  [BhFaxLine.FACR]: P,
  [BhFaxLine.WFAI]: P,
  [BhFaxLine.BAFC]: P,
  [BhFaxLine.SFAC]: P,
  [BhFaxLine.PPO]: P,
  [BhFaxLine.VPG]: P,
};

// DME orders: same assignee table for both the Diabetic Shoe and default routes — only the
// upload folder differs by type.
const DME_ORDER_ASSIGNEES: BhAssignees = {
  ...PHYS_ALL,
  [BhFaxLine.UFAI]: MA,
  [BhFaxLine.PRECISION]: MA,
  [BhFaxLine.FACR]: MA,
  [BhFaxLine.BAFC]: "CA BAFC Non-Custom DME (non-HMO) Authorizations",
  [BhFaxLine.SFAC]: "CA SFAC.PPO.VPG Non-Custom DME (non-HMO) Auth",
  [BhFaxLine.PPO]: "CA SFAC.PPO.VPG Non-Custom DME (non-HMO) Auth",
  [BhFaxLine.VPG]: "CA SFAC.PPO.VPG Non-Custom DME (non-HMO) Auth",
};

// Surgery/COB scheduling row, shared by ORDER_PROCEDURE and NOTIFICATION (Surgery).
const SURGERY_SCHEDULING: BhAssignees = {
  [BhFaxLine.UT]: "UT COB Surgery",
  [BhFaxLine.SOCAL]: P,
  [BhFaxLine.FCC]: P,
  [BhFaxLine.DESERT]: "Perez, Kasandra",
  [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
  [BhFaxLine.ONE_F_TWO_F]: "VA COB Surgery",
  [BhFaxLine.FASWM]: "MI COB Surgery",
  [BhFaxLine.UFAI]: MA,
  [BhFaxLine.IFAC]: "IFACW Surgery Scheduler",
  [BhFaxLine.PRECISION]: "Elizarraz, Yolanda",
  [BhFaxLine.NV]: P,
  [BhFaxLine.FACR]: MA,
  [BhFaxLine.WFAI]: P,
  [BhFaxLine.BAFC]: P,
  [BhFaxLine.SFAC]: P,
  [BhFaxLine.PPO]: P,
  [BhFaxLine.VPG]: P,
};

// ---------------------------------------------------------------------------
// Route types
// ---------------------------------------------------------------------------

/**
 * Extracted-field predicate. Each route's `when` is matched against fields the LLM extracts
 * (documentType, specialty, typeOfLab, signatureRequired, payer). All keys AND-combined;
 * string fields match case-insensitively, `lab` is substring-tolerant.
 */
export interface BalanceHealthRouteWhen {
  type?: string;
  specialty?: string;
  lab?: string;
  insurance?: string;
  signatureRequired?: boolean;
}

export interface BalanceHealthRoute {
  when: BalanceHealthRouteWhen;
  /** Upload folder (consumed by the EHR integration). Null = fall back to the YAML mapping. */
  folder: string | null;
  /** Per-fax-line assignee. A missing entry means "no task, no CloudCruise" on that line. */
  assignees: BhAssignees;
}

function route(when: BalanceHealthRouteWhen, folder: string | null, assignees: BhAssignees): BalanceHealthRoute {
  return { when, folder, assignees };
}

// Shared assignee row for all four IMAGING_RESULTS classifications: PHYS_ALL, minus AFAPS
// (Artisan), with IFAC on the location MA. AFAPS is deliberately dropped — per ops, Artisan
// imaging results are upload-only, so the document is filed in the Radiology Report folder and no
// ModMed task / CloudCruise run is created. Destructured rather than written out so a newly added
// fax line inherits the PHYS_ALL default here too; a spread can override a line but not remove one.
const { [BhFaxLine.AFAPS]: _afapsUploadOnly, ...PHYS_ALL_EXCEPT_AFAPS } = PHYS_ALL;
const IMAGING_RESULTS_ASSIGNEES: BhAssignees = { ...PHYS_ALL_EXCEPT_AFAPS, [BhFaxLine.IFAC]: MA };

// Shared route used by all four IMAGING_RESULTS classifications (CT, MRI, Ultrasound, base).
const imagingResultsRoutes: readonly BalanceHealthRoute[] = [route({}, "Radiology Report", IMAGING_RESULTS_ASSIGNEES)];

// ---------------------------------------------------------------------------
// The table (routes evaluated in order; first match wins, primary route last)
// ---------------------------------------------------------------------------

export const BALANCE_HEALTH_ROUTES: Partial<Record<Classification, readonly BalanceHealthRoute[]>> = {
  [Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY]: [
    route({}, "Results - PT/OT", {
      ...PHYS_ALL,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.PRECISION]: FD,
      [BhFaxLine.FACR]: MA,
    }),
  ],

  [Classification.ADMIN_DISABILITY_FORMS]: [
    route({ type: "Handicap Request" }, "Disability", {
      ...PHYS_ALL,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.PRECISION]: FD,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.BAFC]: MR,
      [BhFaxLine.SFAC]: MR,
      [BhFaxLine.PPO]: MR,
      [BhFaxLine.VPG]: MR,
    }),
    route({}, "Disability", DISABILITY_PRIMARY),
  ],

  // FMLA shares DISABILITY_PRIMARY's values for every existing line, but per ops the new NorCal
  // lines route FMLA to Medical Records (not Front Desk, unlike ADMIN_DISABILITY_FORMS's fallback).
  [Classification.ADMIN_FMLA_FORMS]: [
    route({}, "Disability", {
      ...DISABILITY_PRIMARY,
      [BhFaxLine.BAFC]: MR,
      [BhFaxLine.SFAC]: MR,
      [BhFaxLine.PPO]: MR,
      [BhFaxLine.VPG]: MR,
    }),
  ],

  [Classification.ADMIN_WORK_OR_SCHOOL_PHYSICAL_FORMS]: [
    route({ type: "Status" }, "Disability", {
      [BhFaxLine.UT]: "UT COB Work Comp",
      [BhFaxLine.SOCAL]: P,
      [BhFaxLine.FCC]: "Santana, Tara",
      [BhFaxLine.DESERT]: P,
      [BhFaxLine.AFAPS]: "AFAPS Front Desk",
      [BhFaxLine.ONE_F_TWO_F]: P,
      [BhFaxLine.FASWM]: P,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.PRECISION]: FD,
      [BhFaxLine.NV]: P,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.WFAI]: "Santana, Tara",
      [BhFaxLine.BAFC]: "CA BAFC General Authorizations",
      [BhFaxLine.SFAC]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.PPO]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.VPG]: "CA SFAC.PPO.VPG General Authorizations",
    }),
    route({ type: "Comp" }, "Disability", {
      [BhFaxLine.UT]: "UT COB Work Comp",
      [BhFaxLine.SOCAL]: P,
      [BhFaxLine.FCC]: "Santana, Tara",
      [BhFaxLine.DESERT]: P,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.ONE_F_TWO_F]: P,
      [BhFaxLine.FASWM]: P,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.PRECISION]: FD,
      [BhFaxLine.NV]: P,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.WFAI]: "Santana, Tara",
      [BhFaxLine.BAFC]: MR,
      [BhFaxLine.SFAC]: MR,
      [BhFaxLine.PPO]: MR,
      [BhFaxLine.VPG]: MR,
    }),
  ],

  [Classification.ADMIN_PATIENT_QUESTIONNAIRE]: [route({}, "Custom Device Documents", {})],

  [Classification.ADMIN_TRANSITION_CARE]: [route({}, "Results - Clinical Notes", {})],

  [Classification.CLEARANCE_REQUEST]: [route({}, "Chart Documents", CLEARANCE_ASSIGNEES)],

  [Classification.CLEARANCE_REQUEST_RESPONSE]: [route({}, "Chart Documents", CLEARANCE_ASSIGNEES)],

  [Classification.DME_ORDER]: [
    route({ type: "Diabetic Shoe" }, "Diabetic Shoes", DME_ORDER_ASSIGNEES),
    route({}, "Results - PT/OT", DME_ORDER_ASSIGNEES),
  ],

  [Classification.FINANCIAL]: financialRoutes(),

  [Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS]: [
    route({ type: "Response" }, "Authorization", {
      [BhFaxLine.BAFC]: FD,
      [BhFaxLine.SFAC]: FD,
      [BhFaxLine.PPO]: FD,
      [BhFaxLine.VPG]: FD,
    }),
    route({ type: "Request" }, "Authorization", {
      [BhFaxLine.BAFC]: "CA BAFC General Authorizations",
      [BhFaxLine.SFAC]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.PPO]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.VPG]: "CA SFAC.PPO.VPG General Authorizations",
    }),
  ],

  [Classification.IMAGING_RESULTS]: imagingResultsRoutes,

  [Classification.IMAGING_RESULTS_CT]: imagingResultsRoutes,

  [Classification.IMAGING_RESULTS_MRI]: imagingResultsRoutes,

  [Classification.IMAGING_RESULTS_ULTRASOUND]: imagingResultsRoutes,

  [Classification.LAB_RESULTS]: [...padNetRoutes(), route({}, "Lab Documents", { ...PHYS_ALL, [BhFaxLine.IFAC]: MA })],

  [Classification.LAB_RESULTS_PATHOLOGY]: [
    route({ type: "Surgical" }, "Lab Documents", PHYS_ALL),
    route({}, "Path Report", {
      ...PHYS_ALL,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.PRECISION]: MA,
      [BhFaxLine.BAFC]: "CA BAFC Medical Assistants",
      [BhFaxLine.SFAC]: "CA SFAC Medical Assistants",
      [BhFaxLine.PPO]: "CA PPO Medical Assistants",
      [BhFaxLine.VPG]: "CA VPG Medical Assistants",
    }),
  ],

  [Classification.MEDICAL_HISTORY_HOME_HEALTH]: homeHealthRoutes(),

  [Classification.MEDICAL_RECORD_REQUEST]: [
    route({}, "Legal", {
      [BhFaxLine.UT]: MR,
      [BhFaxLine.SOCAL]: MR,
      [BhFaxLine.FCC]: MR,
      [BhFaxLine.DESERT]: MR,
      [BhFaxLine.AFAPS]: MR,
      [BhFaxLine.ONE_F_TWO_F]: MR,
      [BhFaxLine.FASWM]: MR,
      [BhFaxLine.UFAI]: MR,
      [BhFaxLine.IFAC]: MR,
      [BhFaxLine.PRECISION]: MR,
      [BhFaxLine.NV]: MR,
      [BhFaxLine.FACR]: MR,
      [BhFaxLine.WFAI]: MR,
      [BhFaxLine.BAFC]: MR,
      [BhFaxLine.SFAC]: MR,
      [BhFaxLine.PPO]: MR,
      [BhFaxLine.VPG]: MR,
    }),
  ],

  [Classification.NOTIFICATION]: [
    route({ specialty: "Radiology" }, "Radiology Report", {}),
    route({ specialty: "Wound Care" }, "Chart Documents", {}),
    route({ specialty: "Surgery" }, "Surgical Documents", {
      ...SURGERY_SCHEDULING,
      [BhFaxLine.DESERT]: "Perez, Kasandra",
    }),
  ],

  [Classification.NOTIFICATION_HOSPITAL_ADMIT]: [route({}, "Chart Documents", {})],

  [Classification.ORDER_LAB]: [
    route({}, "Lab Documents", {
      ...PHYS_ALL,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.PRECISION]: MA,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.BAFC]: "CA BAFC Medical Assistants",
      [BhFaxLine.SFAC]: "CA SFAC Medical Assistants",
      [BhFaxLine.PPO]: "CA PPO Medical Assistants",
      [BhFaxLine.VPG]: "CA VPG Medical Assistants",
    }),
  ],

  [Classification.ORDER_PROCEDURE]: [route({}, "Surgical Documents", SURGERY_SCHEDULING)],

  [Classification.OTHER]: [
    route({ type: "Pharmacy Update" }, null, OTHER_OPS_ALL),
    route({ type: "Confirmation of Fax Number" }, null, OTHER_OPS_ALL),
    route({ type: "Employment Verification" }, null, emailAll()),
    route({ type: "Optum Rx" }, null, OTHER_OPS_ALL),
    route({ type: "Repair of Building" }, null, OTHER_OPS_ALL),
    route({ type: "Selling Printer" }, null, OTHER_OPS_ALL),
    route({ type: "Physician Information Verification Form" }, null, OTHER_OPS_ALL),
    route({ type: "Marketing Documents" }, null, OTHER_OPS_ALL),
    // AFAPS routes "Patient Not Found" docs to its New Referral queue instead of a
    // resolved/fallback physician — new-patient intake, not an existing patient's provider.
    route({ type: "Patient Not Found" }, null, { ...PHYS_ALL, [BhFaxLine.AFAPS]: "AFAPS New Referral" }),
    route({ type: "Other" }, null, PHYS_ALL),
  ],

  [Classification.PHARMACY_RELATED]: [
    route({ type: "Unavailable" }, "Prescription Documents", {}),
    route({ type: "Communication" }, "Prescription Documents", PHYS_ALL),
    route({ type: "Copy of Rx" }, "Prescription Documents", {}),
    route({ type: "Scheck & Siress" }, "Results - PT/OT", {
      ...PHYS_ALL,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.PRECISION]: MA,
      [BhFaxLine.FACR]: MA,
    }),
  ],

  [Classification.PHARMACY_RELATED_CARE_CONSIDERATION]: [route({}, "Prescription Documents", PHYS_ALL)],

  [Classification.PHARMACY_RELATED_NON_ADHERENCE]: [route({}, "Prescription Documents", {})],

  [Classification.PHARMACY_RELATED_REFILL]: [
    route({ type: "New" }, "Prescription Documents", {
      ...PHYS_ALL,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.IFAC]: MA,
    }),
    route({ type: "Refill" }, "Prescription Documents", {
      ...PHYS_ALL,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.PRECISION]: MA,
    }),
    route({ type: "90 day" }, "Prescription Documents", {
      ...PHYS_ALL,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.IFAC]: MA,
    }),
    route({ type: "Alternative" }, "Prescription Documents", {
      ...PHYS_ALL,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.IFAC]: MA,
    }),
    route({ type: "Wound VAC" }, "Prescription Documents", {
      [BhFaxLine.UT]: P,
      [BhFaxLine.SOCAL]: P,
      [BhFaxLine.FCC]: P,
      [BhFaxLine.DESERT]: P,
      [BhFaxLine.AFAPS]: P,
      [BhFaxLine.ONE_F_TWO_F]: P,
      [BhFaxLine.FASWM]: P,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.PRECISION]: FD,
      [BhFaxLine.NV]: P,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.WFAI]: P,
    }),
    route({ type: "OAS" }, "Prescription Documents", {}),
    route({ type: "Compound" }, "Prescription Documents", PHYS_ALL),
    route({ type: "Missing Info" }, "Prescription Documents", {
      ...PHYS_ALL,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.FACR]: MA,
    }),
  ],

  [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: [
    route({}, "Prescription Documents", {
      [BhFaxLine.UT]: P,
      [BhFaxLine.SOCAL]: P,
      [BhFaxLine.FCC]: P,
      [BhFaxLine.DESERT]: P,
      [BhFaxLine.AFAPS]: "Garcia, Jess",
      [BhFaxLine.ONE_F_TWO_F]: P,
      [BhFaxLine.FASWM]: P,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.PRECISION]: FD,
      [BhFaxLine.NV]: P,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.WFAI]: "Howard, Brittany",
      [BhFaxLine.BAFC]: "CA BAFC Medical Assistants",
      [BhFaxLine.SFAC]: "CA SFAC Medical Assistants",
      [BhFaxLine.PPO]: "CA PPO Medical Assistants",
      [BhFaxLine.VPG]: "CA VPG Medical Assistants",
    }),
  ],

  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: [route({}, "Prescription Documents", {})],

  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: [
    route({ type: "MRI" }, "Authorization", {
      [BhFaxLine.UT]: P,
      [BhFaxLine.SOCAL]: P,
      [BhFaxLine.FCC]: P,
      [BhFaxLine.DESERT]: P,
      [BhFaxLine.AFAPS]: "COB MRI",
      [BhFaxLine.ONE_F_TWO_F]: "VA COB MRI",
      [BhFaxLine.FASWM]: "COB MRI",
      [BhFaxLine.UFAI]: "COB MRI",
      [BhFaxLine.IFAC]: P,
      [BhFaxLine.PRECISION]: "COB MRI",
      [BhFaxLine.NV]: P,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.WFAI]: "COB MRI",
      [BhFaxLine.BAFC]: "COB MRI",
      [BhFaxLine.SFAC]: "COB MRI",
      [BhFaxLine.PPO]: "COB MRI",
      [BhFaxLine.VPG]: "COB MRI",
    }),
    // COB is specific to the new NorCal lines (BAFC/SFAC/PPO/VPG); no existing line has this cell filled.
    route({ type: "COB" }, "Authorization", {
      [BhFaxLine.BAFC]: "CA BAFC General Authorization",
      [BhFaxLine.SFAC]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.PPO]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.VPG]: "CA SFAC.PPO.VPG General Authorizations",
    }),
    // Default (non-MRI/COB): upload-only into Authorization, no task on any line.
    route({}, "Authorization", {}),
  ],

  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: [
    route({ type: "AFO" }, "Authorization", {
      ...PHYS_ALL,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.PRECISION]: MA,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.BAFC]: "CA BAFC General Authorization",
      [BhFaxLine.SFAC]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.PPO]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.VPG]: "CA SFAC.PPO.VPG General Authorizations",
    }),
    route({ type: "MRI" }, "Authorization", {
      [BhFaxLine.UT]: P,
      [BhFaxLine.SOCAL]: P,
      [BhFaxLine.FCC]: P,
      [BhFaxLine.DESERT]: P,
      [BhFaxLine.AFAPS]: P,
      [BhFaxLine.ONE_F_TWO_F]: "VA COB Surgery",
      [BhFaxLine.FASWM]: "COB MRI",
      [BhFaxLine.UFAI]: "COB MRI",
      [BhFaxLine.IFAC]: P,
      [BhFaxLine.PRECISION]: "COB MRI",
      [BhFaxLine.NV]: P,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.WFAI]: "COB MRI",
      [BhFaxLine.BAFC]: "COB MRI",
      [BhFaxLine.SFAC]: "COB MRI",
      [BhFaxLine.PPO]: "COB MRI",
      [BhFaxLine.VPG]: "COB MRI",
    }),
    route({ type: "Orthotics" }, "Authorization", {
      ...PHYS_ALL,
      [BhFaxLine.UT]: "UT COB Surgery",
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.PRECISION]: MA,
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.BAFC]: "CA BAFC Medical Assistants",
      [BhFaxLine.SFAC]: "CA SFAC Medical Assistants",
      [BhFaxLine.PPO]: "CA PPO Medical Assistants",
      [BhFaxLine.VPG]: "CA VPG Medical Assistants",
    }),
    route({ type: "Surgery" }, "Referral/Authorization Notes", {
      ...PHYS_ALL,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.ONE_F_TWO_F]: "VA COB Surgery",
      [BhFaxLine.FASWM]: "MI COB Surgery",
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.PRECISION]: "Elizarraz, Yolanda",
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.BAFC]: "CA BAFC Surgery",
      [BhFaxLine.SFAC]: "CA SFAC Surgery",
      [BhFaxLine.PPO]: "CA PPO Surgery",
      [BhFaxLine.VPG]: "CA VPG Surgery",
    }),
    // Default (no procedure type): FACR + the new NorCal lines create a task.
    route({}, "Authorization", {
      [BhFaxLine.FACR]: MA,
      [BhFaxLine.BAFC]: "CA BAFC General Authorization",
      [BhFaxLine.SFAC]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.PPO]: "CA SFAC.PPO.VPG General Authorizations",
      [BhFaxLine.VPG]: "CA SFAC.PPO.VPG General Authorizations",
    }),
  ],

  [Classification.PROGRESS_NOTE]: [
    route({ specialty: "Physical Therapy", signatureRequired: true }, "Results - PT/OT", {
      ...PHYS_ALL,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.PRECISION]: MA,
    }),
    route({ specialty: "Physical Therapy", type: "TLC Pain Management" }, "Chart Documents", {}),
    route({ specialty: "Physical Therapy" }, "Results - PT/OT", {
      ...PHYS_ALL,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.PRECISION]: FD,
    }),
    route({ specialty: "Occupational Therapy", signatureRequired: true }, "Results - PT/OT", {
      ...PHYS_ALL,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.PRECISION]: MA,
    }),
    route({ specialty: "Occupational Therapy" }, "Results - PT/OT", {
      ...PHYS_ALL,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.PRECISION]: FD,
    }),
    route({ specialty: "Wound Care" }, "Results - PT/OT", {
      ...PHYS_ALL,
      [BhFaxLine.UFAI]: MA,
      [BhFaxLine.PRECISION]: MA,
    }),
    // Vascular is specific to the new NorCal lines; no existing line has this cell filled.
    route({ specialty: "Vascular" }, "Chart Documents", {
      [BhFaxLine.BAFC]: P,
      [BhFaxLine.SFAC]: P,
      [BhFaxLine.PPO]: P,
      [BhFaxLine.VPG]: P,
    }),
    // Catch-all: any progress note needing a signature goes to the provider.
    route({ signatureRequired: true }, "Results - PT/OT", PHYS_ALL),
    // Primary: consult notes, filed only (no task).
    route({}, "Chart Documents", {}),
  ],

  [Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY]: [
    route({}, "Chart Documents", {
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.BAFC]: P,
      [BhFaxLine.SFAC]: P,
      [BhFaxLine.PPO]: P,
      [BhFaxLine.VPG]: P,
    }),
  ],

  [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]: [
    route({}, "Surgical Documents", {
      [BhFaxLine.DESERT]: P,
      [BhFaxLine.AFAPS]: "AFAPS Medical Assistants",
      [BhFaxLine.IFAC]: MA,
      [BhFaxLine.BAFC]: "CA BAFC Surgery",
      [BhFaxLine.SFAC]: "CA SFAC Surgery",
      [BhFaxLine.PPO]: "CA PPO Surgery",
      [BhFaxLine.VPG]: "CA VPG Surgery",
    }),
  ],

  [Classification.PROGRESS_NOTE_PRE_OPERATIVE]: [
    route({}, "Surgical Documents", {
      ...PHYS_ALL,
      [BhFaxLine.ONE_F_TWO_F]: "VA COB Surgery",
      [BhFaxLine.FASWM]: "MI COB Surgery",
    }),
  ],

  [Classification.REFERRAL_RESPONSE]: [route({}, "Referral/Authorization Notes", {})],

  [Classification.REFERRAL_STATUS_REQUEST]: [route({}, "Referral/Authorization Notes", {})],

  [Classification.IDENTIFICATION_CARD]: [route({}, "Patient ID Card", {})],

  [Classification.INSURANCE_CARD]: [route({}, "Patient ID Card", {})],
};

function financialRoutes(): readonly BalanceHealthRoute[] {
  const billingAll: BhAssignees = {
    [BhFaxLine.UT]: "Billing Inquiries",
    [BhFaxLine.SOCAL]: "Billing Inquiries",
    [BhFaxLine.FCC]: "Billing Inquiries",
    [BhFaxLine.DESERT]: "Billing Inquiries",
    [BhFaxLine.AFAPS]: "Billing Inquiries",
    [BhFaxLine.ONE_F_TWO_F]: "Billing Inquiries",
    [BhFaxLine.FASWM]: "Billing Inquiries",
    [BhFaxLine.UFAI]: "Billing Inquiries",
    [BhFaxLine.IFAC]: "Billing Inquiries",
    [BhFaxLine.PRECISION]: "Billing Inquiries",
    [BhFaxLine.NV]: "Billing Inquiries",
    [BhFaxLine.FACR]: "Billing Inquiries",
    [BhFaxLine.WFAI]: "Billing Inquiries",
    [BhFaxLine.BAFC]: "Billing Inquiries",
    [BhFaxLine.SFAC]: "Billing Inquiries",
    [BhFaxLine.PPO]: "Billing Inquiries",
    [BhFaxLine.VPG]: "Billing Inquiries",
  };

  // Waiver of Liability routes SoCal to the RCM call center; all other financial types use billing.
  return [
    "Waiver of Liability",
    "Payment Transmittal",
    "Payment Expiring Soon",
    "Explanation of Payment",
    "Explanation of Benefits",
    "Expedited Payment",
  ].map((type) =>
    route(
      { type },
      null,
      type === "Waiver of Liability" ? { ...billingAll, [BhFaxLine.SOCAL]: "RCM Call Center" } : billingAll
    )
  );
}

function padNetRoutes(): readonly BalanceHealthRoute[] {
  const padAssignees: BhAssignees = {
    ...PHYS_ALL,
    [BhFaxLine.UFAI]: MA,
    [BhFaxLine.PRECISION]: FD,
    [BhFaxLine.FACR]: MA,
    [BhFaxLine.BAFC]: "CA BAFC Medical Assistants",
    [BhFaxLine.SFAC]: "CA SFAC Medical Assistants",
    [BhFaxLine.PPO]: "CA PPO Medical Assistants",
    [BhFaxLine.VPG]: "CA VPG Medical Assistants",
  };

  return ["Peripheral Arterial Disease", "Ankle-Brachial Index", "Pulse Volume Recording", "ABI"].map((lab) =>
    route({ lab }, "PADnet", padAssignees)
  );
}

function homeHealthRoutes(): readonly BalanceHealthRoute[] {
  const signed: BhAssignees = { ...PHYS_ALL, [BhFaxLine.UFAI]: MA, [BhFaxLine.PRECISION]: MA };

  return [
    route({ type: "Plan of Care", signatureRequired: true }, "Results - PT/OT", signed),
    route({ type: "Plan of Care" }, "Chart Documents", {}),
    route({ type: "Progress Note", signatureRequired: true }, "Results - PT/OT", signed),
    route({ type: "Progress Note" }, "Chart Documents", {}),
    route({ type: "Rx", signatureRequired: true }, "Results - PT/OT", signed),
    route({ type: "Rx" }, "Orders", {}),
    route({ signatureRequired: true }, "Results - PT/OT", signed),
  ];
}

function emailAll(): BhAssignees {
  const email = "Humanresources@weil4feet.com";

  return {
    [BhFaxLine.UT]: email,
    [BhFaxLine.SOCAL]: email,
    [BhFaxLine.FCC]: email,
    [BhFaxLine.DESERT]: email,
    [BhFaxLine.AFAPS]: email,
    [BhFaxLine.ONE_F_TWO_F]: email,
    [BhFaxLine.FASWM]: email,
    [BhFaxLine.UFAI]: email,
    [BhFaxLine.IFAC]: email,
    [BhFaxLine.PRECISION]: email,
    [BhFaxLine.NV]: email,
    [BhFaxLine.FACR]: email,
    [BhFaxLine.BAFC]: email,
    [BhFaxLine.SFAC]: email,
    [BhFaxLine.PPO]: email,
    [BhFaxLine.VPG]: email,
  };
}

// ---------------------------------------------------------------------------
// Matching / resolution
// ---------------------------------------------------------------------------

function eqi(a: string | undefined, b: string | undefined): boolean {
  return (a ?? "").toLowerCase().trim() === (b ?? "").toLowerCase().trim();
}

function matchesPredicate(when: BalanceHealthRouteWhen, fields: BalanceHealthRouteWhen): boolean {
  for (const key of Object.keys(when) as (keyof BalanceHealthRouteWhen)[]) {
    const expected = when[key];

    if (expected === undefined) continue;

    if (key === "signatureRequired") {
      if (fields.signatureRequired !== expected) return false;
      continue;
    }

    if (key === "lab") {
      // Lab names vary; tolerate substring matches (e.g. "Peripheral Arterial Disease").
      const actual = (fields.lab ?? "").toLowerCase().trim();
      const exp = String(expected).toLowerCase().trim();

      if (actual.length === 0 || (!actual.includes(exp) && !exp.includes(actual))) return false;
      continue;
    }

    if (!eqi(fields[key] as string | undefined, expected as string)) return false;
  }

  return true;
}

/** Pulls routing-relevant fields out of a section's content (EHR integration path). */
export function getExtractedRoutingFields(
  enrichedContent: Record<string, unknown> | null | undefined,
  reviewedContent?: Record<string, unknown> | null | undefined
): BalanceHealthRouteWhen {
  const docInfo = readReviewedFields(enrichedContent?.documentInformation);
  const referral = readReviewedFields(enrichedContent?.referralInformation);
  const primaryCoverage = readReviewedFields(enrichedContent?.primaryCoverage);

  const fields: BalanceHealthRouteWhen = {};
  const type = stringField(reviewedContent?.documentType) ?? stringField(docInfo?.documentType);

  if (type !== null) fields.type = type;
  const specialty =
    stringField(referral?.specialty) ?? stringField(reviewedContent?.specialty) ?? stringField(docInfo?.specialty);

  if (specialty !== null) fields.specialty = specialty;
  const lab = stringField(reviewedContent?.typeOfLab) ?? stringField(docInfo?.typeOfLab);

  if (lab !== null) fields.lab = lab;
  const insurance = stringField(readPayerName(primaryCoverage));

  if (insurance !== null) fields.insurance = insurance;
  const signatureRequired = reviewedContent?.signatureRequired ?? docInfo?.signatureRequired;

  if (typeof signatureRequired === "boolean") fields.signatureRequired = signatureRequired;

  return fields;
}

/** Pulls routing-relevant fields off `section.reviewedContent` (doc-instruction path). */
export function getRoutingFieldsFromReviewedContent(
  reviewedContent: Record<string, unknown> | null | undefined
): BalanceHealthRouteWhen {
  const fields: BalanceHealthRouteWhen = {};
  const type = stringField(reviewedContent?.documentType) ?? stringField(reviewedContent?.type);

  if (type !== null) fields.type = type;
  const specialty = stringField(reviewedContent?.specialty);

  if (specialty !== null) fields.specialty = specialty;
  const lab =
    stringField(reviewedContent?.labType) ??
    stringField(reviewedContent?.typeOfLab) ??
    stringField(reviewedContent?.nameOfLab);

  if (lab !== null) fields.lab = lab;
  const insurance = stringField(reviewedContent?.insurance) ?? stringField(reviewedContent?.payer);

  if (insurance !== null) fields.insurance = insurance;
  if (reviewedContent?.signatureRequired === true) fields.signatureRequired = true;

  return fields;
}

function readReviewedFields(block: unknown): Record<string, unknown> | null {
  if (!block || typeof block !== "object") return null;
  const reviewed = (block as { reviewedFields?: unknown }).reviewedFields;

  return reviewed && typeof reviewed === "object" ? (reviewed as Record<string, unknown>) : null;
}

function readPayerName(coverage: Record<string, unknown> | null): unknown {
  const payer = coverage?.payer;

  if (payer && typeof payer === "object") {
    return (payer as { name?: unknown }).name ?? null;
  }

  return null;
}

function stringField(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Resolve a Balance Health route from the extracted fields (fax-line-independent).
 * Returns null when the classification has no routes or no predicate matches.
 */
export function resolveBalanceHealthRoute(
  classification: Classification,
  fields: BalanceHealthRouteWhen
): BalanceHealthRoute | null {
  const routes = BALANCE_HEALTH_ROUTES[classification];

  if (!routes || routes.length === 0) return null;
  for (const r of routes) {
    if (matchesPredicate(r.when, fields)) return r;
  }

  return null;
}

/**
 * Resolve the route and the raw (unresolved) assignee for a given fax line. `rawAssignee` is
 * `null` when the fax line's cell is empty — meaning no task and no CloudCruise. Sentinels
 * (see `ASSIGNEE`) are resolved to concrete names in `configuration.ts`.
 */
export function resolveBalanceHealthRouting(
  classification: Classification,
  faxLine: BhFaxLine,
  fields: BalanceHealthRouteWhen
): { route: BalanceHealthRoute; rawAssignee: string | null } | null {
  const route = resolveBalanceHealthRoute(classification, fields);

  if (!route) return null;

  return { route, rawAssignee: route.assignees[faxLine] ?? null };
}
